import { E as EVENTS } from './events-55e4fd80.js';
import { a as atBreakpointChange } from './at-breakpoint-change-929474bb.js';
import { g as getMediaQuery } from './media-queries-5a9283a8.js';
import { P as PauseableInterval } from './pauseable-interval-9c9bee89.js';
import './srraf.es-487187f3.js';

const {
  SLIDESHOW_PAGINATION_CONNECTED,
  SLIDESHOW_PAGINATION_BULLET_CLICKED,
  SLIDESHOW_PAGINATION_SET_ACTIVE_BULLET,
  SLIDESHOW_PAGINATION_START_AUTOPLAY,
  SLIDESHOW_PAGINATION_PAUSE_AUTOPLAY,
  SLIDESHOW_PAGINATION_RESUME_AUTOPLAY,
  SLIDESHOW_PAGINATION_AUTOPLAY_ADVANCE,
  SLIDESHOW_PAGINATION_NAV_CLICK,
} = EVENTS;

class SlideshowPagination extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      parentSection: ".section",
      pagination: "[js-slideshow-pagination]",
      paginationBullet: "[js-slideshow-pagination-bullet]",
      paginationNav: "[js-slideshow-pagination-nav]",
      slideshowPause: "[js-slideshow-pause]",
    };

    this.classes = {
      active: "active",
    };

    this.strings = window.theme.strings;
  }

  async connectedCallback() {
    this.sectionId = this.getAttribute("data-section-id");
    this.navigationBreakpointName = this.getAttribute(
      "data-navigation-breakpoint-name",
    );
    this.navigationBreakpointValue = this.getAttribute(
      "data-navigation-breakpoint-value",
    );
    this.navigationBreakpointQuery = getMediaQuery(
      this.navigationBreakpointName,
    );
    this.controller = new AbortController();
    this.eventOptions = { signal: this.controller.signal };

    // Listen for bullet clicks
    this.paginationBullets = this.querySelectorAll(
      this.selectors.paginationBullet,
    );
    this.paginationBullets.forEach((bullet) => {
      bullet.addEventListener(
        "click",
        this.handlePaginationBulletClick.bind(this),
        this.eventOptions,
      );
    });

    // Listen for active bullet update
    window.addEventListener(
      SLIDESHOW_PAGINATION_SET_ACTIVE_BULLET(this.sectionId),
      this.handlePaginationBulletUpdate.bind(this),
      this.eventOptions,
    );

    // Listen for autoplay events
    if (this.getAttribute("data-enable-autoplay") === "true") {
      this.initAutoplay();

      window.addEventListener(
        SLIDESHOW_PAGINATION_START_AUTOPLAY(this.sectionId),
        this.startAutoplay.bind(this),
        this.eventOptions,
      );

      window.addEventListener(
        SLIDESHOW_PAGINATION_PAUSE_AUTOPLAY(this.sectionId),
        this.pauseAutoplay.bind(this),
        this.eventOptions,
      );

      window.addEventListener(
        SLIDESHOW_PAGINATION_RESUME_AUTOPLAY(this.sectionId),
        this.resumeAutoplay.bind(this),
        this.eventOptions,
      );
    }

    // Update whether the navigation buttons are active
    atBreakpointChange(
      this.navigationBreakpointValue,
      this.handleWindowResize.bind(this),
    );
    this.handleWindowResize();

    this.parentSectionTagName = this.closest(
      this.selectors.parentSection,
    ).tagName.toLowerCase();

    // Due to how our JS files load, it's possible that this component can load before its
    // parent, so we need to wait for the parent to be defined before we continue.
    //
    // For example, if this component exists in multiple sections, the first instance will
    // load after its parent section, however any subsequent instances will load before their
    // parent because this component has already been defined. Refer to https://stackoverflow.com/a/79571621
    // for more context.
    //
    // Check whether the tag name includes a "-" to ensure that the parent section is a custom element.
    if (this.parentSectionTagName.includes("-")) {
      await customElements.whenDefined(this.parentSectionTagName);
    }

    // Emit that pagination is connected
    const connectedEvent = new Event(
      SLIDESHOW_PAGINATION_CONNECTED(this.sectionId),
    );
    window.dispatchEvent(connectedEvent);
  }

  handleWindowResize() {
    if (window.matchMedia(this.navigationBreakpointQuery).matches) {
      this.destroyNav();
    } else {
      this.initNav();
    }
  }

  handlePaginationBulletClick(event) {
    const clickedIndex = event.currentTarget.getAttribute("data-slide-index");
    const bulletClickEvent = new CustomEvent(
      SLIDESHOW_PAGINATION_BULLET_CLICKED(this.sectionId),
      { detail: { index: clickedIndex } },
    );

    window.dispatchEvent(bulletClickEvent);
  }

  handlePaginationBulletUpdate(event) {
    this.setActiveBullet(event.detail.index);
  }

  setActiveBullet(index) {
    this.paginationBullets.forEach((bullet) => {
      if (bullet.getAttribute("data-slide-index") === index) {
        bullet.classList.add(this.classes.active);
      } else {
        bullet.classList.remove(this.classes.active);
      }
    });

    if (this.autoplay?.isPaused()) {
      this.autoplay.pausedTimeLeft = this.timeBetweenSlides;
    } else {
      this.autoplay.reset();
    }
  }

  initNav() {
    this.navController = new AbortController();
    this.paginationNavButtons = this.querySelectorAll(
      this.selectors.paginationNav,
    );

    this.paginationNavButtons.forEach((button) => {
      button.addEventListener(
        "click",
        this.handlePaginationNavClick.bind(this),
        { signal: this.navController.signal },
      );
    });
  }

  destroyNav() {
    this.navController?.abort();
  }

  handlePaginationNavClick(event) {
    const navEvent = new CustomEvent(
      SLIDESHOW_PAGINATION_NAV_CLICK(this.sectionId),
      {
        detail: {
          direction: event.currentTarget.dataset.direction,
        },
      },
    );
    window.dispatchEvent(navEvent);
  }

  initAutoplay() {
    // Setting is in seconds, so we need to convert
    this.timeBetweenSlides =
      parseInt(this.getAttribute("data-time-between-slides"), 10) * 1000;
    this.slideshowPause = this.querySelector(this.selectors.slideshowPause);

    // When sections are re-ordered in the theme editor, the custom element is only
    // disconnected, so data remains in place.
    if (!this.autoplay) {
      this.autoplay = new PauseableInterval(() => {
        const autoplayAdvanceEvent = new Event(
          SLIDESHOW_PAGINATION_AUTOPLAY_ADVANCE(this.sectionId),
        );

        window.dispatchEvent(autoplayAdvanceEvent);
      }, this.timeBetweenSlides);
    }

    this.autoplay.clear();

    this.slideshowPause?.addEventListener(
      "click",
      this.handlePauseClick.bind(this),
      { signal: this.controller.signal },
    );
  }

  startAutoplay() {
    this.autoplay.reset();
  }

  handlePauseClick(event) {
    if (this.getAttribute("data-paused") === "false") {
      this.setAttribute("data-paused", "true");
      event.currentTarget.setAttribute(
        "aria-label",
        this.strings.accessibility.carouselPlay,
      );
      this.autoplay.pause();
    } else {
      this.setAttribute("data-paused", "false");
      event.currentTarget.setAttribute(
        "aria-label",
        this.strings.accessibility.carouselPause,
      );
      this.autoplay.resume();
    }
  }

  pauseAutoplay() {
    this.autoplay.pause();
    this.setAttribute("data-paused", "true");
    this.slideshowPause?.setAttribute(
      "aria-label",
      this.strings.accessibility.carouselPlay,
    );
  }

  resumeAutoplay() {
    // If we're not paused, resuming can throw off timing
    if (this.autoplay.isPaused()) {
      this.autoplay.resume();
      this.setAttribute("data-paused", "false");
      this.slideshowPause?.setAttribute(
        "aria-label",
        this.strings.accessibility.carouselPause,
      );
    }
  }

  disconnectedCallback() {
    this.controller.abort();
    this.autoplay?.clear();
  }
}

if (!customElements.get("slideshow-pagination")) {
  customElements.define("slideshow-pagination", SlideshowPagination);
}
